// src/pages/Admin/ReglementInscription/components/ReglementFiltersBar.jsx

import React, { useEffect, useRef, useState } from "react";
import { FaFilter, FaSearch, FaTimes } from "react-icons/fa";

import { fmtEUR, STATUTS_REGLEMENT } from "../reglement.helpers";

export default function ReglementFiltersBar({
  searchTerm,
  setSearchTerm,
  selectedSeason,
  setSelectedSeason,
  seasons = [],
  passSportFilter,
  setPassSportFilter,
  passSportOptions = [],
  statusFilters,
  setStatusFilters,
  selectedGroup,
  setSelectedGroup,
  groups = [],
  filteredCount,
  totalAffiche,
  resetFilters,
}) {
  const [statusOpen, setStatusOpen] = useState(false);
  const statusRef = useRef(null);

  useEffect(() => {
    if (!statusOpen) return;

    const handleClickOutside = (event) => {
      if (statusRef.current && !statusRef.current.contains(event.target)) {
        setStatusOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [statusOpen]);

  const toggleStatus = (status) => {
    setStatusFilters((current) => {
      if (current.includes(status)) {
        return current.filter((item) => item !== status);
      }

      return [...current, status];
    });
  };

  return (
    <div className="border-b border-slate-200 p-4 dark:border-slate-800 sm:p-5 lg:p-6">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Tableau des règlements
          </h2>

          <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {Number(filteredCount || 0).toLocaleString("fr-FR")} règlement
            {filteredCount > 1 ? "s" : ""} affiché{filteredCount > 1 ? "s" : ""} · total{" "}
            <span className="font-bold text-slate-900 dark:text-white">
              {fmtEUR(totalAffiche)}
            </span>
          </p>
        </div>

        <button
          type="button"
          onClick={resetFilters}
          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FaTimes />
          Réinitialiser
        </button>
      </div>

      <div className="mt-4 grid gap-2 xl:grid-cols-[minmax(260px,1.5fr)_repeat(4,minmax(160px,1fr))]">
        <div className="relative xl:col-span-2">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Rechercher nom, prénom, saison, observation..."
            className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          />
        </div>

        <select
          value={selectedSeason}
          onChange={(event) => setSelectedSeason(event.target.value)}
          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
        >
          <option value="">Toutes saisons</option>
          {seasons.map((season) => (
            <option key={season} value={season}>
              {season}
            </option>
          ))}
        </select>

        <select
          value={passSportFilter}
          onChange={(event) => setPassSportFilter(event.target.value)}
          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
        >
          <option value="">Pass'Sport tous</option>
          <option value="nonzero">Pass'Sport renseigné</option>
          {passSportOptions.map((value) => (
            <option key={String(value)} value={String(value)}>
              {fmtEUR(value)}
            </option>
          ))}
        </select>

        <div className="relative" ref={statusRef}>
          <button
            type="button"
            onClick={() => setStatusOpen((value) => !value)}
            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-left text-sm font-bold text-slate-700 outline-none transition hover:bg-slate-50 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
          >
            {statusFilters.length === 0
              ? "Tous statuts"
              : `Statuts (${statusFilters.length})`}
          </button>

          {statusOpen ? (
            <div className="absolute z-[9999] mt-2 w-72 overflow-hidden rounded-[22px] border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-950">
              <div className="flex gap-2 border-b border-slate-200 p-3 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setStatusFilters([...STATUTS_REGLEMENT])}
                  className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  Tout
                </button>

                <button
                  type="button"
                  onClick={() => setStatusFilters([])}
                  className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  Effacer
                </button>
              </div>

              <div className="max-h-72 overflow-auto p-2">
                {STATUTS_REGLEMENT.map((status) => (
                  <label
                    key={status}
                    className="flex cursor-pointer items-center gap-3 rounded-2xl px-3 py-2 transition hover:bg-slate-50 dark:hover:bg-slate-900"
                  >
                    <input
                      type="checkbox"
                      checked={statusFilters.includes(status)}
                      onChange={() => toggleStatus(status)}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                    />

                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      {status}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <select
          value={selectedGroup}
          onChange={(event) => setSelectedGroup(event.target.value)}
          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
        >
          <option value="">Tous groupes</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.nom}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4 rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-xs leading-5 text-indigo-800 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
        <span className="inline-flex items-center gap-2 font-bold">
          <FaFilter />
          Astuce :
        </span>{" "}
        l’export Excel reprend exactement les filtres appliqués à l’écran.
      </div>
    </div>
  );
}